import { IPriceBreakdownList } from "@/features/assistant/types";
import { roundToTwoDecimals } from "./calculateQuoteTotals";

interface VssTotalsCartItem {
  price: IPriceBreakdownList;
  quantity: number;
}

interface CalculateVssTotalsParams {
  cartItems: VssTotalsCartItem[];
  /** VAT % from account's dealer currency */
  vatPercentage?: number;
  /** Delivery charge to include in the VAT base (not added flat on top) */
  deliveryCharge?: number;
}

export interface VssTotals {
  /** Sum of VSS prices (discount already applied per-product) × quantity */
  vssSubtotal: number;
  /** Total discount amount across all cart items */
  vssDiscountAmount: number;
  /** Same as vssSubtotal — discount is already reflected in vssTotalPrice */
  vssSubtotalAfterDiscount: number;
  /** VAT applied on vssSubtotal */
  vssVat: number;
  /** Final VSS total (vssSubtotal + vssVat) */
  vssTotalAmount: number;
}

export const calculateVssTotals = ({
  cartItems,
  vatPercentage = 0,
  deliveryCharge = 0,
}: CalculateVssTotalsParams): VssTotals => {
  let vssSubtotal = 0;
  let vssDiscountAmount = 0;

  for (const item of cartItems) {
    const qty = item.quantity;
    const unitPrice = item.price.vssTotalPrice ?? 0;
    const vssFlatCharge = item.price.vssFlatChargeTotal ?? 0;
    const discountPerUnit = item.price.totalVssDiscountAmount ?? 0;

    // Flat surcharge must not be multiplied by qty — same treatment as flatChargeTotal on the dealer side.
    const scaledVss = vssFlatCharge > 0
      ? (unitPrice - vssFlatCharge) * qty + vssFlatCharge
      : unitPrice * qty;

    vssSubtotal += roundToTwoDecimals(scaledVss);
    vssDiscountAmount += roundToTwoDecimals(discountPerUnit * qty);
  }

  vssSubtotal = roundToTwoDecimals(vssSubtotal);
  vssDiscountAmount = roundToTwoDecimals(vssDiscountAmount);
  const vssSubtotalAfterDiscount = roundToTwoDecimals(vssSubtotal - vssDiscountAmount);
  const vssVatBase = roundToTwoDecimals(vssSubtotalAfterDiscount + deliveryCharge);
  const vssVat = roundToTwoDecimals(vssVatBase * (vatPercentage / 100));
  const vssTotalAmount = roundToTwoDecimals(vssVatBase + vssVat);

  return {
    vssSubtotal,
    vssDiscountAmount,
    vssSubtotalAfterDiscount,
    vssVat,
    vssTotalAmount,
  };
};
